const PAD = 48;
const MIN_VIEW_METERS = 40;
const MERCATOR_HALF = 20037508.342789244;

export type GmlMercatorBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

export type GmlPreviewFrame = {
  scale: number;
  project: (coord: number[]) => [number, number];
  /** Web Mercator bounds of the whole canvas. Null when the coordinates are not longitude/latitude. */
  mercatorBounds: GmlMercatorBounds | null;
};

export type OrthophotoTilePlacement = {
  z: number;
  x: number;
  y: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

export function lngLatToMercator(lng: number, lat: number): [number, number] {
  const x = (lng * MERCATOR_HALF) / 180;
  const clamped = Math.max(-85, Math.min(85, lat));
  const y = Math.log(Math.tan(((90 + clamped) * Math.PI) / 360)) * (MERCATOR_HALF / Math.PI);
  return [x, y];
}

function isLngLat(coords: Array<[number, number]>): boolean {
  return coords.every(([x, y]) => Math.abs(x) <= 180 && Math.abs(y) <= 90);
}

/** Fit a drawing to the canvas in metres, so a parcel keeps its real shape. */
export function fitGmlPreview(
  coords: Array<[number, number]>,
  width: number,
  height: number,
): GmlPreviewFrame {
  const geographic = isLngLat(coords);
  const plane = coords.map(([x, y]) => (geographic ? lngLatToMercator(x, y) : [x, y] as [number, number]));
  const xs = plane.map((coord) => coord[0]);
  const ys = plane.map((coord) => coord[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const lat = geographic ? coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length : 55.7;
  const mercatorPerMeter = geographic ? 1 / Math.max(Math.cos((lat * Math.PI) / 180), 0.2) : 1;
  const minSpan = MIN_VIEW_METERS * mercatorPerMeter;
  const spanX = Math.max(maxX - minX, minSpan);
  const spanY = Math.max(maxY - minY, minSpan);
  const scale = Math.min((width - PAD * 2) / spanX, (height - PAD * 2) / spanY);
  const usedW = spanX * scale;
  const usedH = spanY * scale;
  const originX = (width - usedW) / 2;
  const originY = (height - usedH) / 2;
  const viewMinX = cx - spanX / 2;
  const viewMaxY = cy + spanY / 2;
  const canvasMinX = viewMinX - originX / scale;
  const canvasMaxY = viewMaxY + originY / scale;
  const mercatorBounds = geographic
    ? {
      minX: canvasMinX,
      maxX: canvasMinX + width / scale,
      maxY: canvasMaxY,
      minY: canvasMaxY - height / scale,
    }
    : null;
  return {
    scale,
    mercatorBounds,
    project: (coord: number[]): [number, number] => {
      const [px, py] = geographic ? lngLatToMercator(coord[0], coord[1]) : [coord[0], coord[1]];
      return [
        originX + (px - viewMinX) * scale,
        originY + (viewMaxY - py) * scale,
      ];
    },
  };
}

/** Orthophoto tiles covering the canvas, in the same Web Mercator frame as the drawing. */
export function orthophotoTilePlacements(
  bounds: GmlMercatorBounds,
  canvasWidth: number,
  canvasHeight: number,
): OrthophotoTilePlacement[] {
  const spanX = bounds.maxX - bounds.minX;
  const spanY = bounds.maxY - bounds.minY;
  if (spanX <= 0 || spanY <= 0) {
    return [];
  }
  const world = MERCATOR_HALF * 2;
  const metersPerPixel = spanX / canvasWidth;
  const z = Math.max(1, Math.min(19, Math.ceil(Math.log2(world / (256 * metersPerPixel)))));
  const tileWorld = world / (2 ** z);
  const x0 = Math.max(0, Math.floor((bounds.minX + MERCATOR_HALF) / tileWorld));
  const x1 = Math.min((2 ** z) - 1, Math.floor((bounds.maxX + MERCATOR_HALF) / tileWorld));
  const y0 = Math.max(0, Math.floor((MERCATOR_HALF - bounds.maxY) / tileWorld));
  const y1 = Math.min((2 ** z) - 1, Math.floor((MERCATOR_HALF - bounds.minY) / tileWorld));
  const placements: OrthophotoTilePlacement[] = [];
  for (let x = x0; x <= x1; x += 1) {
    for (let y = y0; y <= y1; y += 1) {
      const west = -MERCATOR_HALF + x * tileWorld;
      const north = MERCATOR_HALF - y * tileWorld;
      placements.push({
        z,
        x,
        y,
        left: ((west - bounds.minX) / spanX) * canvasWidth,
        top: ((bounds.maxY - north) / spanY) * canvasHeight,
        width: (tileWorld / spanX) * canvasWidth,
        height: (tileWorld / spanY) * canvasHeight,
      });
      if (placements.length > 36) {
        return placements;
      }
    }
  }
  return placements;
}
